import type { CfcConditionCode } from "../Cfc/cfcConditions";

export const EXPANDED_CARRIER_PANEL = {
  CARRIER_113_CUSTOM: "carrier_113_custom",
  CARRIER_130: "carrier_130",
  CARRIER_611: "carrier_611",
} as const;

export type ExpandedCarrierPanel =
  (typeof EXPANDED_CARRIER_PANEL)[keyof typeof EXPANDED_CARRIER_PANEL];

export type ExpandedCarrierPanelAccountFlag =
  | "is130PanelAccount"
  | "is611PanelAccount"
  | "is113CustomPanelAccount";

export const EXPANDED_CARRIER_PANEL_ACCOUNT_FLAGS: Record<
  ExpandedCarrierPanel,
  ExpandedCarrierPanelAccountFlag
> = {
  [EXPANDED_CARRIER_PANEL.CARRIER_113_CUSTOM]: "is113CustomPanelAccount",
  [EXPANDED_CARRIER_PANEL.CARRIER_130]: "is130PanelAccount",
  [EXPANDED_CARRIER_PANEL.CARRIER_611]: "is611PanelAccount",
};

export const DRAFT_STATUS = {
  DRAFT: "draft",
  REDRAW_DRAFT: "redraw_draft",
  SUBMITTED_REDRAW: "submitted_redraw",
  SUBMITTED: "submitted",
  DISMISSED: "dismissed",
  EMPTY: "",
} as const;

export type DraftStatus = (typeof DRAFT_STATUS)[keyof typeof DRAFT_STATUS];

// The mutually-exclusive billing options shown in the New Order flow.
export enum BillingType {
  Insurance = "Insurance",
  Patient = "Patient",
  Client = "Client",
  PendingMedicaid = "Pending Medicaid",
  CashPay = "Cash Pay",
}

export interface ShippingInformationRequest {
  first_name: string;
  last_name: string;
  street_address: string;
  apt_suite: string;
  city: string;
  state: string;
  zip_code: string;
}

export interface NewRequisitionType {
  // patient info
  is_provider_authorized: boolean;
  is_ship_to_patient?: boolean;
  shipping_information?: ShippingInformationRequest;
  draft_status?: DraftStatus;
  first_name: string;
  middle_initial?: string;
  last_name: string;
  sex: "Male" | "Female" | "";
  maternal_weight: string;
  medical_record_number?: string;
  date_of_birth: string;
  estimated_due_date: string | null;
  collection_date?: string;

  created_at?: number;
  cell_phone: string;
  email_address: string;
  street_address: string;
  apt_suite: string;
  city: string;
  state: string;
  zip_code: string;
  is_aneuploidy_include_22q: boolean;
  is_O28_3: boolean;
  is_vanishing_twin: boolean;
  abnormal_ultrasound_findings: string;
  // ethnicity string broken into booleans
  is_asian: boolean;
  is_african: boolean;
  is_ashkenazi: boolean;
  is_middle_eastern: boolean;
  is_french_canadian: boolean;
  is_hispanic: boolean;
  is_northern_european: boolean;
  is_white: boolean;
  is_other_ethnicity: boolean;
  is_unknown_ethnicity: boolean;

  // family history info
  family_history_of_genetic_disorders: string;
  is_reported_carrier_patient_alpha: boolean;
  is_reported_carrier_patient_sickle: boolean;
  is_reported_carrier_patient_SMA: boolean;
  is_reported_carrier_patient_cf: boolean;
  reported_carrier_patient_variant: string;
  is_reported_carrier_fob_alpha: boolean;
  is_reported_carrier_fob_sickle: boolean;
  is_reported_carrier_fob_SMA: boolean;
  is_reported_carrier_fob_cf: boolean;
  reported_carrier_fob_variant: string;

  reproductive_partner_first_name: string;
  reproductive_partner_last_name: string;
  reproductive_partner_date_of_birth: string | null;
  is_reproductive_partner_maternal: boolean;
  is_reproductive_partner_paternal: boolean;

  // test info
  unity_complete_id: string;
  is_carrier_screen_order: boolean;
  expanded_carrier_panel?: ExpandedCarrierPanel | null;
  is_cfc_order?: boolean;
  selected_conditions?: CfcConditionCode[];
  is_rhd_only_order: boolean;
  is_aneuploidy_order: boolean;
  is_aneuploidy_twin_nipt: boolean;
  is_aneuploidy_opt_out_fetal_sex: boolean;
  is_aneuploidy_include_rhd: boolean;
  is_fragile_x: boolean;
  is_acog_plus: boolean;
  is_fetal_antigen: boolean;
  is_fetal_antigen_rbc: boolean;
  is_fetal_antigen_platelet: boolean;
  is_fetal_antigen_big_c: boolean;
  is_fetal_antigen_little_c: boolean;
  is_fetal_antigen_d: boolean;
  is_fetal_antigen_duffy: boolean;
  is_fetal_antigen_e: boolean;
  is_fetal_antigen_kell: boolean;
  is_fetal_antigen_little_e: boolean;
  is_fetal_antigen_fyb: boolean;
  is_fetal_antigen_jka: boolean;
  is_fetal_antigen_jkb: boolean;
  is_fetal_antigen_little_k: boolean;
  is_fetal_antigen_m: boolean;
  is_fetal_antigen_n: boolean;
  is_fetal_antigen_big_s: boolean;
  is_fetal_antigen_little_s: boolean;
  is_fetal_antigen_u: boolean;
  is_fetal_antigen_hpa_1a: boolean;
  is_fetal_antigen_hpa_1b: boolean;
  is_fetal_antigen_hpa_2a: boolean;
  is_fetal_antigen_hpa_2b: boolean;
  is_fetal_antigen_hpa_3a: boolean;
  is_fetal_antigen_hpa_3b: boolean;
  is_fetal_antigen_hpa_4a: boolean;
  is_fetal_antigen_hpa_4b: boolean;
  is_fetal_antigen_hpa_5a: boolean;
  is_fetal_antigen_hpa_5b: boolean;
  is_fetal_antigen_hpa_9a: boolean;
  is_fetal_antigen_hpa_9b: boolean;
  is_fetal_antigen_hpa_15a: boolean;
  is_fetal_antigen_hpa_15b: boolean;
  is_pregnant: boolean;
  is_twin: boolean;
  is_triplet_or_more: boolean;
  is_egg_donor: boolean;
  age_of_egg_donor: number;
  opt_out_cf_dna: boolean;

  // ICD 10 definitions
  // carrier screen with fetal risk assessment
  is_Z84_81: boolean;
  is_Z31_430: boolean;
  is_Z31_440: boolean;
  is_Z81_0: boolean;
  is_O28_5_carrier: boolean;
  is_Z34_81_carrier: boolean;
  is_Z34_82_carrier: boolean;
  is_Z34_83_carrier: boolean;
  // aneuploidy nipt
  is_O09_511: boolean;
  is_O09_512: boolean;
  is_O09_513: boolean;
  is_O09_521: boolean;
  is_O09_522: boolean;
  is_O09_523: boolean;
  is_O09_891: boolean;
  is_O09_892: boolean;
  is_O09_893: boolean;
  is_O28_5_aneuploidy: boolean;
  is_Z34_81_aneuploidy: boolean;
  is_Z34_82_aneuploidy: boolean;
  is_Z34_83_aneuploidy: boolean;
  is_Z34_01: boolean;
  is_Z34_02: boolean;
  is_Z34_03: boolean;
  is_Z36_0: boolean;
  // rhd nipt
  is_O36_0110: boolean;
  is_O36_0120: boolean;
  is_O36_0130: boolean;
  is_Z31_82: boolean;
  // fetal antigen nipt
  is_O36_1910: boolean;
  is_O36_1920: boolean;
  is_O36_1930: boolean;
  // (comma separated values if more than one apply)
  other_icd_10_codes: string;
  other_carrier_screen_icd_10_codes: string;
  other_aneuploidy_icd_10_codes: string;
  other_rhd_icd_10_codes: string;
  other_fetal_antigen_icd_10_codes: string;
  // billing info
  is_standard_account: boolean;
  is_mobile_phlebotomy_account: boolean;
  is_patient_sheet_account: boolean;
  is_insurance_sheet_account: boolean;
  is_international_account: boolean;
  is_fetal_antigen_account: boolean;
  is_fragile_x_account: boolean;
  is_rhd_only_account: boolean;
  is_130_panel_account?: boolean;
  is_611_panel_account?: boolean;
  is_113_custom_panel_account?: boolean;
  is_mobile_phlebotomy: boolean;
  is_bill_to_insurance: boolean;
  is_bill_to_patient: boolean;
  is_bill_to_client: boolean;
  is_pending_medicaid?: boolean;
  is_cash_pay?: boolean;
  bill_to_insurance_type?: "Entry" | "Attach" | "Upload";
  ins_card_urls?: string[];
  fetal_antigen_urls?: string[];
  insurance_company_name?: string;
  group_id?: string;
  member_id?: string;
  policy_owner_first_name?: string;
  policy_owner_last_name?: string;
  policy_owner_dob?: string | null;
  preferred_language?: string;
  relationship_to_insured?: "Self" | "Dependent" | "Other" | "";
  lims_id: string;
  npi: string;
  ordering_health_care_providers: string;
  clinic_phone: string;
  clinic_fax: string;
  clinic_name: string;
  clinic_additional_notes: string;
  is_patient_sheet_attached: boolean;
  is_ins_sheet_attached: boolean;
  treatment_notes: string;
  treatment_notes_download_url?: string;

  // provider user info
  provider_full_name?: string;
  source_req_identifier?: string;
  is_redraw_alert_dismissed?: boolean;

  // edit-order read metadata
  is_editable?: boolean;
  edit_window_expires_at?: string;
  last_edited_at?: string;
  last_edited_by?: string;
}

export interface NewReqClinicAccount {
  lims_id: string;
  sfname: string;
  sfid: string;
  phone: string;
  office_fax: string;
  billingstreet: string;
  is_standard_account: boolean;
  is_mobile_phlebotomy_account: boolean;
  is_international_account: boolean;
  is_patient_sheet_account: boolean;
  is_insurance_sheet_account: boolean;
  is_fetal_antigen_account: boolean;
  is_fragile_x_account: boolean;
  is_rhd_only_account: boolean;
  is_130_panel_account: boolean;
  is_611_panel_account: boolean;
  is_113_custom_panel_account: boolean;
}

export interface NewReqOrderingProvider {
  npi: string;
  physician_name: string;
  lims_id: string;
}

export type OrderingProviderLookupError =
  | "not_found"
  | "ambiguous"
  | "request_failed";

interface OrderingProviderLookupSuccess {
  providers: NewReqOrderingProvider[];
  limsId: string;
  accountName: string;
  isMobilePhlebotomyAccount: boolean;
  error?: undefined;
}

interface OrderingProviderLookupFailure {
  providers: [];
  error: OrderingProviderLookupError;
}

export type OrderingProviderLookupResult =
  | OrderingProviderLookupSuccess
  | OrderingProviderLookupFailure;

export enum StepperIndex {
  Clinic = 0,
  Patient = 1,
  Indication = 2,
  Billing = 3,
  Shipping = 4,
  Preflight = 5,
  Confirmed = 6,
}

export const statesArray = [
  "AK - Alaska",
  "AL - Alabama",
  "AR - Arkansas",
  "AS - American Samoa",
  "AZ - Arizona",
  "CA - California",
  "CO - Colorado",
  "CT - Connecticut",
  "DC - District of Columbia",
  "DE - Delaware",
  "FL - Florida",
  "GA - Georgia",
  "GU - Guam",
  "HI - Hawaii",
  "IA - Iowa",
  "ID - Idaho",
  "IL - Illinois",
  "IN - Indiana",
  "KS - Kansas",
  "KY - Kentucky",
  "LA - Louisiana",
  "MA - Massachusetts",
  "MD - Maryland",
  "ME - Maine",
  "MI - Michigan",
  "MN - Minnesota",
  "MO - Missouri",
  "MS - Mississippi",
  "MT - Montana",
  "NC - North Carolina",
  "ND - North Dakota",
  "NE - Nebraska",
  "NH - New Hampshire",
  "NJ - New Jersey",
  "NM - New Mexico",
  "NV - Nevada",
  "NY - New York",
  "OH - Ohio",
  "OK - Oklahoma",
  "OR - Oregon",
  "PA - Pennsylvania",
  "PR - Puerto Rico",
  "RI - Rhode Island",
  "SC - South Carolina",
  "SD - South Dakota",
  "TN - Tennessee",
  "TX - Texas",
  "UT - Utah",
  "VA - Virginia",
  "VI - Virgin Islands",
  "VT - Vermont",
  "WA - Washington",
  "WI - Wisconsin",
  "WV - West Virginia",
  "WY - Wyoming",
];

export const phlebotomyLanguageOptions = [
  "English",
  "Spanish",
  "Other",
] as const;
